import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppShell } from './shell/AppShell';
import { OverviewScreen } from './screens/Overview/OverviewScreen';
import { FleetScreen } from './screens/Fleet/FleetScreen';
import { DevicesScreen } from './screens/Devices/DevicesScreen';
import { AlertsScreen } from './screens/Alerts/AlertsScreen';
import { TasksScreen } from './screens/Tasks/TasksScreen';
import { AccessScreen } from './screens/Access/AccessScreen';
import { AuditScreen } from './screens/Audit/AuditScreen';
import { ProfileScreen } from './screens/Profile/ProfileScreen';

const MetricsScreen = lazy(() =>
  import('./screens/Metrics/MetricsScreen').then((m) => ({ default: m.MetricsScreen })),
);
const SiteSettingsScreen = lazy(() =>
  import('./screens/SiteSettings/SiteSettingsScreen').then((m) => ({
    default: m.SiteSettingsScreen,
  })),
);
const SupportScreen = lazy(() =>
  import('./screens/Support/SupportScreen').then((m) => ({ default: m.SupportScreen })),
);
import { ActivityScreen } from './screens/Activity/ActivityScreen';
import { LoginScreen } from './screens/Auth/LoginScreen';
import { CallbackScreen } from './screens/Auth/CallbackScreen';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { RoleGuard } from './auth/RoleGuard';
import { METRICS_ROLES, SITE_SETTINGS_VIEW_ROLES } from './screens/Metrics/metrics-access';
// Static, and small: it holds the lazy import of the 3D screen itself.
import { TwinRoute } from './screens/Twin/TwinRoute';

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/callback" element={<CallbackScreen />} />
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/overview" replace />} />
        <Route path="/overview" element={<OverviewScreen />} />
        <Route path="/fleet" element={<FleetScreen />} />
        <Route path="/devices" element={<DevicesScreen />} />
        <Route
          path="/metrics"
          element={
            <RoleGuard allowedRoles={METRICS_ROLES} fallback={<Navigate to="/overview" replace />}>
              <Suspense fallback={null}>
                <MetricsScreen />
              </Suspense>
            </RoleGuard>
          }
        />
        {/* Per-site settings for Metrics (labour rate, cleaning targets,
            zone groups, the go-live date). Supervisors and admins edit;
            platform support (support_l2/l3) reads it with no edit
            controls (D12). The server refuses anyone else's write
            regardless. */}
        <Route
          path="/site-settings"
          element={
            <RoleGuard
              allowedRoles={SITE_SETTINGS_VIEW_ROLES}
              fallback={<Navigate to="/overview" replace />}
            >
              <Suspense fallback={null}>
                <SiteSettingsScreen />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route path="/tasks" element={<TasksScreen />} />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfileScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/activity"
          element={
            <ProtectedRoute>
              <ActivityScreen />
            </ProtectedRoute>
          }
        />
        {/*
          The 3D building view's old address. UNLISTED: not in `NAV_ENTRIES`,
          no hotkey; the demo role's avatar menu is the one link to it.

          Bare `/twin` is the showcase for the demo role, `service_operator`
          (Decision 19). Every other role, and any locate parameter,
          redirects to `/overview?view=3d` with its parameters, where the
          Building view lives beside the live facts (`TwinRoute`,
          `twin-route.ts`).

          `service_operator` is NOT a read-only role: it holds
          `door.open`/`door.close` and the full `lift.*` set
          (`policy/capabilities.py`), so the lift and door drawers this view
          opens render their verbs for it, exactly as they do on the
          Overview. Some hotel staff hold it (Q3).

          The runtime flag still gates the showcase; with it off, `/twin`
          redirects like any other link, and the Overview gives the reason
          it shows the Map (`twin-availability.ts`).
        */}
        <Route path="/twin" element={<TwinRoute />} />
        <Route
          path="/alerts"
          element={
            <RoleGuard
              allowedRoles={[
                'nexsoss:platform_admin',
                'nexsoss:site_supervisor',
                'nexsoss:service_operator',
              ]}
              fallback={<Navigate to="/overview" replace />}
            >
              <AlertsScreen />
            </RoleGuard>
          }
        />
        <Route
          path="/access"
          element={
            <RoleGuard
              allowedRoles={['nexsoss:platform_admin']}
              fallback={<Navigate to="/overview" replace />}
            >
              <AccessScreen />
            </RoleGuard>
          }
        />
        <Route
          path="/audit"
          element={
            <RoleGuard
              allowedRoles={['nexsoss:platform_admin', 'nexsoss:site_supervisor']}
              fallback={<Navigate to="/overview" replace />}
            >
              <AuditScreen />
            </RoleGuard>
          }
        />
        <Route path="/profile" element={<ProfileScreen />} />
        <Route
          path="/support"
          element={
            <RoleGuard
              allowedRoles={[
                'nexsoss:platform_admin',
                'nexsoss:site_supervisor',
                'nexsoss:service_operator',
              ]}
              fallback={<Navigate to="/overview" replace />}
            >
              <Suspense fallback={null}>
                <SupportScreen />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route path="*" element={<Navigate to="/overview" replace />} />
      </Route>
    </Routes>
  );
}
